import React from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme
} from '@mui/material'
import PsychologyIcon from '@mui/icons-material/Psychology'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import BlockIcon from '@mui/icons-material/Block'
import { useHandleChangeRoute } from 'data/hooks'

const AI_FEATURES = [
  {
    id: 'ai-chatbot',
    icon: PsychologyIcon,
    title: 'Ask Stethi Anything',
    description:
      'Our AI assistant Stethi can answer questions about any job posting—compensation, call schedule, culture, and more—so you can decide if a role is right for you before applying.',
  },
  {
    id: 'smart-alerts',
    icon: NotificationsActiveIcon,
    title: 'Smart Job Alerts',
    description:
      'Set detailed filters once and get notified the moment a matching position is posted. Never miss a role that fits your criteria.',
  },
  {
    id: 'no-spam',
    icon: BlockIcon,
    title: 'Zero Recruiter Spam',
    description:
      'Every listing connects you directly to in-house hospital recruiters. No third-party middlemen, no cold calls from agencies—just direct, meaningful connections.',
  },
]

const AiFeaturesSection = () => {
  const theme = useTheme()
  const handleChangeRoute = useHandleChangeRoute()

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.dark,
        color: theme.palette.common.white,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.primary.light,
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.9rem',
              display: 'block',
              mb: 2,
            }}
          >
            AI-POWERED FEATURES
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.common.white,
              fontSize: { xs: '2rem', md: '2.75rem' },
              lineHeight: 1.2,
              mb: 3,
            }}
          >
            Your Job Search, Supercharged by AI
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: '1.1rem',
              lineHeight: 1.7,
              maxWidth: '640px',
              mx: 'auto',
            }}
          >
            MyStethi combines AI-powered tools with direct hospital access to make your job search faster, smarter, and completely spam-free.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {AI_FEATURES.map((feature) => {
            const Icon = feature.icon
            return (
              <Grid item xs={12} md={4} key={feature.id}>
                <Card
                  sx={{
                    backgroundColor: 'transparent',
                    border: `1px solid rgba(255,255,255,0.15)`,
                    borderRadius: '16px',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.common.white,
                        mb: 3,
                      }}
                    >
                      <Icon />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.common.white,
                        mb: 1.5,
                        fontSize: '1.1rem',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 8 } }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleChangeRoute('/register/physicians')()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              fontWeight: 600,
              px: 5,
              py: 1.75,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: theme.palette.shadows.colored.primary,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.palette.shadows.colored.primaryHover,
                transform: 'translateY(-2px)',
              },
            }}
          >
            Get Started
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

export default AiFeaturesSection
